import { useEffect, useState } from "react";
import { useI18n } from "@/i18n";
import { loungeCopy } from "@/lib/loungeCopy";
import api from "@/lib/api";
import { toast } from "sonner";
import { LoungeShell, LoungeSectionTitle } from "@/components/lounge/LoungeShell";
import { LoungeHero, BrandIntro, Gallery } from "@/components/lounge/LoungeSections";
import { LoungeMediaLibrary } from "@/components/lounge/LoungeMediaLibrary";
import { SampleSelection } from "@/components/lounge/SampleSelection";
import { ReleaseDetailDialog } from "@/components/lounge/ReleaseDetailDialog";
import { PressSubscribeBox } from "@/components/lounge/PressSubscribeBox";
import { useAutoTranslate, AutoTranslatedBadge } from "@/lib/loungeTranslate";
import { resolveLoungeUrl } from "@/lib/loungeUrl";
import { formatReleaseDate } from "@/lib/releaseDate";
import { Button } from "@/components/ui/button";
import { Download, ArrowRight, Copy, BookOpen, ShieldCheck } from "lucide-react";
import { PressGateProvider, usePressGate, triggerDownload } from "@/components/lounge/PressAccreditationGate";
import { trackLoungeEvent } from "@/lib/loungeTrack";

const ACCREDIT_BADGE = {
  it: "Accreditato · download sbloccati",
  en: "Accredited · downloads unlocked",
  fr: "Accrédité · téléchargements débloqués",
  de: "Akkreditiert · Downloads freigeschaltet",
  es: "Acreditado · descargas desbloqueadas",
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function useRef_() {
  const [ref] = useState(() => {
    try {
      const r = new URLSearchParams(window.location.search).get("ref") || "";
      if (r) sessionStorage.setItem("lounge_ref", r);
      return r || sessionStorage.getItem("lounge_ref") || "";
    } catch {
      return "";
    }
  });
  return ref;
}

export default function PressLounge() {
  const { lang } = useI18n();
  return (
    <PressGateProvider lang={lang}>
      <PressLoungeContent lang={lang} />
    </PressGateProvider>
  );
}

function PressLoungeContent({ lang }) {
  const c = loungeCopy(lang);
  const ref = useRef_();
  const { gate, accredited } = usePressGate();
  const [data, setData] = useState({ hero: "", grid: [], releases: [], media: {}, languages: [], intro: {}, guidelines: {}, pr_contacts: {} });
  const [openRel, setOpenRel] = useState(null);

  useEffect(() => {
    document.title = "Press & Media Lounge · Brema 1969";
    api.get("/public/lounge/press").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  // Regole materiale: usa il testo compilato per la lingua corrente; in mancanza,
  // traduci automaticamente il testo IT (fallback) nella lingua del visitatore.
  const glDedicated = (data.guidelines || {})[lang] || [];
  const glItSource = (data.guidelines || {})["it"] || [];
  const glNeedTr = lang !== "it" && glDedicated.length === 0 && glItSource.length > 0;
  const { translated: glTr, translating: glTranslating } = useAutoTranslate(glNeedTr ? glItSource : [], lang);
  const guidelines = glDedicated.length > 0 ? glDedicated : (glNeedTr ? glTr : glItSource);

  // Comunicati stampa: traduci automaticamente titolo + estratto + categoria (sorgente IT)
  // nella lingua del visitatore. PDF/ZIP restano nel file originale.
  const releases = data.releases || [];
  const prContacts = data.pr_contacts || {};
  const relSource = [];
  releases.forEach((r) => { relSource.push(r.title || "", r.excerpt || "", r.category || ""); });
  const relNeedTr = lang !== "it" && releases.length > 0;
  const { translated: relTr, translating: relTranslating } = useAutoTranslate(relNeedTr ? relSource : [], lang);

  const copyRelease = async (title, excerpt) => {
    try { await navigator.clipboard.writeText(`${title}\n\n${excerpt}`.trim()); toast.success(c.copied); }
    catch { toast.error(c.copy_failed); }
  };

  return (
    <LoungeShell active="press" languages={data.languages}>
      <LoungeHero image={data.hero} eyebrow={c.press_eyebrow} title={(data.hero_title || {})[lang] || c.press_title} subtitle={(data.hero_sub || {})[lang] || c.press_sub}>
        <a href="#comunicati">
          <Button className="rounded-full bg-primary px-6 font-semibold text-primary-foreground hover:bg-primary/90" data-testid="lounge-hero-releases-btn">
            {c.cta_press} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </a>
        <a href="#campioni">
          <Button variant="outline" className="rounded-full border-border bg-card px-6 text-foreground hover:bg-brema-hover" data-testid="lounge-hero-material-btn">
            {c.cta_material}
          </Button>
        </a>
        {accredited && (
          <span data-testid="press-accredited-badge" className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3.5 py-1.5 text-sm font-semibold text-emerald-600 dark:text-emerald-400">
            <ShieldCheck className="h-4 w-4" /> {ACCREDIT_BADGE[lang] || ACCREDIT_BADGE.it}
          </span>
        )}
      </LoungeHero>

      <BrandIntro intro={data.intro} />
      <Gallery images={data.grid} />

      {/* Comunicati stampa */}
      <section id="comunicati" className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-24" data-testid="lounge-releases">
        <LoungeSectionTitle eyebrow="Newsroom" title={c.releases_title} subtitle={c.releases_sub} testId="lounge-releases-title" />
        {relNeedTr && (
          <div className="mb-6" data-testid="lounge-releases-translated">
            <AutoTranslatedBadge translating={relTranslating} labels={c} testId="lounge-releases-translated-badge" />
          </div>
        )}
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {data.releases.map((r, i) => {
            const rTitle = relNeedTr && !relTranslating ? (relTr[i * 3] ?? r.title) : r.title;
            const rExcerpt = relNeedTr && !relTranslating ? (relTr[i * 3 + 1] ?? r.excerpt) : r.excerpt;
            const rCategory = relNeedTr && !relTranslating ? (relTr[i * 3 + 2] ?? r.category) : r.category;
            return (
            <article
              key={r.id}
              className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card"
              data-testid={`lounge-release-card-${i}`}
            >
              {r.cover_url && (
                <button type="button" onClick={() => setOpenRel(r)} className="aspect-[16/9] w-full overflow-hidden bg-muted" data-testid={`lounge-release-cover-${i}`}>
                  <img src={resolveLoungeUrl(r.cover_url)} alt={rTitle} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 hover:scale-105" />
                </button>
              )}
              <div className="flex flex-1 flex-col p-6">
                <div className="mb-3 flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="rounded bg-brand/15 px-2 py-0.5 font-semibold text-brand">{rCategory}</span>
                  <span data-testid={`lounge-release-date-${i}`}>{formatReleaseDate(r.date, lang)}</span>
                </div>
                <button type="button" onClick={() => setOpenRel(r)} className="text-left text-lg font-semibold leading-snug text-foreground transition-colors hover:text-brand" data-testid={`lounge-release-title-${i}`}>
                  {rTitle}
                </button>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{rExcerpt}</p>

                <Button onClick={() => { setOpenRel(r); trackLoungeEvent({ area: "press", type: "view", item: { kind: "release", id: r.id, title: r.title }, lang }); }} variant="ghost" size="sm" className="mt-4 h-auto w-fit gap-1.5 px-0 text-sm font-semibold text-brand hover:bg-transparent hover:text-brand/80" data-testid={`lounge-release-readmore-${i}`}>
                  <BookOpen className="h-4 w-4" /> {c.read_more}
                </Button>

                <div className="mt-4 flex flex-wrap gap-2 border-t border-border pt-4">
                  {r.zip_url && (
                    <Button size="sm" variant="outline" onClick={() => gate(() => triggerDownload(`${BACKEND}${r.zip_url}`), { type: "download", item: { kind: "release_zip", id: r.id, title: r.title, filename: "media-kit.zip" } })} className="rounded-full border-border bg-transparent text-foreground hover:bg-brema-hover" data-testid={`lounge-release-zip-${i}`}>
                      <Download className="mr-1.5 h-3.5 w-3.5" /> {c.download_zip}
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => copyRelease(rTitle, rExcerpt)} className="rounded-full border-border bg-transparent text-foreground hover:bg-brema-hover" data-testid={`lounge-release-copy-${i}`} title={c.copy_text} aria-label={c.copy_text}>
                    <Copy className="mr-1.5 h-3.5 w-3.5" /> {c.copy_text}
                  </Button>
                </div>
              </div>
            </article>
            );
          })}
        </div>
      </section>

      <ReleaseDetailDialog release={openRel} open={!!openRel} onOpenChange={(v) => { if (!v) setOpenRel(null); }} contacts={prContacts} c={c} lang={lang} />

      <PressSubscribeBox c={c} refValue={ref} lang={lang} />

      {/* Libreria multimediale */}
      <LoungeMediaLibrary media={data.media} />

      {guidelines.length > 0 && (
        <section className="mx-auto max-w-6xl px-5 pt-4 sm:px-8" data-testid="lounge-material-rules">
          <div className="rounded-2xl border border-border bg-card p-5 sm:p-6">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-bold uppercase tracking-wide text-brand">{c.material_rules_title}</h3>
              {glNeedTr && <AutoTranslatedBadge translating={glTranslating} labels={c} testId="lounge-material-rules-translated-badge" />}
            </div>
            <ul className="space-y-2">
              {guidelines.map((g, i) => (
                <li key={i} className="flex gap-2.5 text-sm text-muted-foreground" data-testid={`lounge-material-rule-${i}`}>
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
                  <span>{g}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <SampleSelection sourcePage="press" refValue={ref} />
    </LoungeShell>
  );
}
